import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  Dispatch,
  AnyAction,
  Middleware,
  MiddlewareAPI,
  SerializedError,
} from '@reduxjs/toolkit';
import find from 'lodash/find';

import { RootState, AppDispatch, AppThunk, AppThunkConfig } from '../../app/store';
import { push, route_path, matchPath, generatePath } from '../../app/route';
import { bindAPI } from '../../app/auth';
import {
  logoutAction,
  loginWithPasswordAction,
  loginWithAuthorizationCodeAction,
  getTokenInfoAction,
} from '../auth/authSlice';
import {
  listTenantsAction,
  selectTenants,
} from '../tenant/tenantSlice';
import {
  GetProfileResponse,
  getProfile,
} from '../../handlers';

export type UserState = {
  status: 'idle'|'loading'|'failed'|'success';
  current_tenant_id: string;
  profile?: {
    id: string;
    username: string;
    allowed_grant_type: 'password'|'refresh_token';
  };
  error?: SerializedError;
};

const initialState: UserState = {
  status: 'idle',
  current_tenant_id: '',
};

export const getProfileAction = createAsyncThunk<
  GetProfileResponse, void, AppThunkConfig
>(
  'user/getProfile',
  (arg, thunkAPI) => {
    return bindAPI(getProfile, thunkAPI)(arg);
  }
);

export const switchTenantAction = (tenant_id: string): AppThunk => (
  dispatch,
  getState
) => {
  const current_tenant_id = selectCurrentTenantId(getState());
  if (current_tenant_id === tenant_id) {
    return;
  }
  const tenants = selectTenants(getState());
  if (find(tenants, { tenant_id })) {
    dispatch(setCurrentTenantId(tenant_id));
  } else if (tenants.length) {
    dispatch(setCurrentTenantId(tenants[0].tenant_id));
  } else {
    dispatch(push(route_path.tenant_create));
  }
};

export const toRoutePath = (path: string): AppThunk => (
  dispatch, getState
) => {
  const tenant_id = selectCurrentTenantId(getState());
  if (!tenant_id) {
    return;
  }
  let to_url = generatePath(path, { tenant_id });
  dispatch(push(to_url));
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentTenantId(state: UserState, action: PayloadAction<string>) {
      state.current_tenant_id = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfileAction.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getProfileAction.fulfilled, (state, action) => {
        state.status = 'success';
        state.profile = {
          id: action.payload.id,
          username: action.payload.username,
          allowed_grant_type: action.payload.allowed_grant_type,
        };
      })
      .addCase(getProfileAction.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error;
      })
      .addCase(logoutAction, () => initialState);
  },
});

export const { setCurrentTenantId } = userSlice.actions;

export const userMiddleware: Middleware = (
  { dispatch, getState }: MiddlewareAPI<AppDispatch, RootState>
) => {
  return (next: Dispatch) => {
    return (action: AnyAction) => {
      let result = next(action);
      if (loginWithPasswordAction.fulfilled.match(action) ||
          getTokenInfoAction.fulfilled.match(action) ||
          loginWithAuthorizationCodeAction.fulfilled.match(action)
      ) {
        dispatch(listTenantsAction());
      } else if (listTenantsAction.fulfilled.match(action)) {
        const tenants = selectTenants(getState());
        const current_tenant_id = selectCurrentTenantId(getState());
        if (!tenants.length) {
          dispatch(push(route_path.tenant_create));
        } else if (!current_tenant_id) {
          const { location } = getState().router;
          if (location.pathname === route_path.tenant_create) {
            dispatch(switchTenantAction(tenants[0].tenant_id));
            dispatch(toRoutePath(route_path.home));
          } else if (location.pathname === route_path.login) {
            dispatch(switchTenantAction(tenants[0].tenant_id));
            dispatch(toRoutePath(route_path.home));
          } else {
            const is_match = matchPath<{ tenant_id: string }>(location.pathname, {
              path: route_path.tenant_info,
              exact: false,
            });
            if (is_match) {
              dispatch(switchTenantAction(is_match.params.tenant_id));
            } else {
              dispatch(switchTenantAction(tenants[0].tenant_id));
              dispatch(toRoutePath(route_path.home));
            }
          }
        }
      }
      return result;
    }
  };
};

export const selectCurrentTenantId = (state: RootState) => state.user.current_tenant_id;
export const selectCurrentTenant = (state: RootState) => {
  if (state.user.current_tenant_id) {
    for (let tenant of selectTenants(state)) {
      if (tenant.tenant_id === state.user.current_tenant_id) {
        return tenant;
      }
    }
  }
}
